// Redux reducer for User KPI Management

import * as types from './redux.actions';

// Helper function to normalize date format to YYYY-MM-DD
const normalizeDateFormat = (dateString) => {
    if (!dateString) return dateString;
    // If date includes time (ISO format), extract just the date part
    if (dateString.includes('T')) {
        return dateString.split('T')[0];
    }
    return dateString;
};

// Helper function to normalize score object dates
const normalizeScoreDate = (score) => {
    return {
        ...score,
        scoreDate: normalizeDateFormat(score.scoreDate)
    };
};

const initialState = {
    userKpis: [],
    selectedUserKpi: null,
    userKpiWithGoals: null,
    userKpiGoals: [],
    userKpiScores: [],
    loading: false,
    dialogLoading: false, // Separate loading for dialog operations
    error: null,
    // Dropdown data
    allUsers: [],
    usersForEdit: [], // Separate list for Edit Dialog using old endpoint
    allPeriods: [],
    allTemplates: [],
    allGoals: [],
    allDepartments: [],
    allDesignations: [],
    allReportingManagers: [],
    allDirectLineManagers: [],
    loadingDropdowns: false,
    filters: {
        search: '',
        userId: '',
        periodId: '',
        departmentId: '',
        reportingManager: '',
        directLineManager: '',
        sortBy: 'updatedAt',
        sortDescending: true
    },
    pagination: {
        limit: 10,
        offset: 1,
        total: 0,
        totalPages: 0
    },
    dialogs: {
        userKpiDialog: false,
        manageGoalsDialog: false,
        deleteDialog: false,
        editUserKpiDialog: false,
        scoreDialog: false
    },
    dialogData: {
        userKpi: null,
        userKpiToDelete: null
    }
};

const userKpiReducer = (state = initialState, action) => {
    switch (action.type) {
        case types.FETCH_USER_KPIS_REQUEST:
        case types.ASSIGN_USER_KPI_REQUEST:
        case types.DELETE_USER_KPI_REQUEST:
        case types.UPDATE_USER_KPI_REQUEST:
            // Main table loading
            return {
                ...state,
                loading: true,
                error: null
            };

        case types.FETCH_USER_KPI_WITH_GOALS_REQUEST:
        case types.UPDATE_USER_KPI_GOAL_REQUEST:
        case types.UPDATE_MULTIPLE_GOALS_REQUEST:
        case types.REMOVE_GOAL_FROM_USER_KPI_REQUEST:
        case types.FETCH_USER_KPI_GOALS_REQUEST:
        case types.FETCH_USER_KPI_SCORES_REQUEST:
            // Dialog loading - does NOT affect main table
            return {
                ...state,
                dialogLoading: true,
                error: null
            };

        case types.ADD_GOAL_TO_USER_KPI_REQUEST:
            // Don't set dialogLoading for ADD_GOAL - it has its own dialog with loading state
            // Setting dialogLoading would hide the entire Score Management table
            return {
                ...state,
                error: null
            };

        case types.FETCH_USER_KPI_BY_ID_REQUEST:
            // NOTE: Score CUD operations (CREATE_SCORE, UPDATE_SCORE, DELETE_SCORE, CREATE_BULK_SCORES) 
            // do NOT set loading to prevent table flicker during optimistic updates
            return {
                ...state,
                loading: true,
                error: null
            };

        case types.FETCH_USER_KPIS_SUCCESS:
            return {
                ...state,
                loading: false,
                userKpis: action.payload.data,
                pagination: {
                    ...state.pagination,
                    ...action.payload.pagination
                },
                error: null
            };

        case types.FETCH_USER_KPI_BY_ID_SUCCESS:
            return {
                ...state,
                loading: false,
                selectedUserKpi: action.payload,
                error: null
            };

        case types.FETCH_USER_KPI_WITH_GOALS_SUCCESS:
            // Update both userKpiWithGoals and the corresponding user KPI in the userKpis array
            const updatedUserKpis = state.userKpis.map(userKpi =>
                userKpi.id === action.payload.id
                    ? { 
                        ...userKpi, 
                        goals: action.payload.goals || [],
                        goalsCount: (action.payload.goals || []).length
                      }
                    : userKpi
            );
            
            return {
                ...state,
                dialogLoading: false,
                userKpiWithGoals: action.payload,
                userKpiGoals: action.payload.goals || [],
                userKpis: updatedUserKpis,
                error: null
            };

        case types.ASSIGN_USER_KPI_SUCCESS:
            return {
                ...state,
                loading: false,
                userKpis: [action.payload, ...state.userKpis],
                dialogs: {
                    ...state.dialogs,
                    userKpiDialog: false
                },
                error: null
            };

        case types.DELETE_USER_KPI_SUCCESS:
            return {
                ...state,
                loading: false,
                userKpis: state.userKpis.filter(kpi => kpi.id !== action.payload),
                dialogs: {
                    ...state.dialogs,
                    deleteDialog: false
                },
                dialogData: {
                    ...state.dialogData,
                    userKpiToDelete: null
                },
                error: null
            };

        case types.UPDATE_USER_KPI_SUCCESS:
            return {
                ...state,
                loading: false,
                userKpis: state.userKpis.map(kpi =>
                    kpi.id === action.payload.id ? { ...kpi, ...action.payload } : kpi
                ),
                dialogs: {
                    ...state.dialogs,
                    editUserKpiDialog: false
                },
                dialogData: {
                    ...state.dialogData,
                    userKpi: null
                },
                error: null
            };

        case types.ADD_GOAL_TO_USER_KPI_SUCCESS:
            return {
                ...state,
                // Don't change dialogLoading - it wasn't set in REQUEST
                userKpiGoals: [...state.userKpiGoals, action.payload],
                // Update the goals count in the main userKpis array
                userKpis: state.userKpis.map(kpi =>
                    kpi.id === action.payload.userKpiId
                        ? { ...kpi, goals: [...(kpi.goals || []), action.payload] }
                        : kpi
                ),
                // Update userKpiWithGoals if it's the same user KPI
                userKpiWithGoals: state.userKpiWithGoals && state.userKpiWithGoals.id === action.payload.userKpiId
                    ? { ...state.userKpiWithGoals, goals: [...(state.userKpiWithGoals.goals || []), action.payload] }
                    : state.userKpiWithGoals,
                error: null
            };

        case types.UPDATE_USER_KPI_GOAL_SUCCESS:
            return {
                ...state,
                dialogLoading: false,
                userKpiGoals: state.userKpiGoals.map(goal =>
                    goal.id === action.payload.id ? action.payload : goal
                ),
                // Update the goal in the main userKpis array
                userKpis: state.userKpis.map(kpi =>
                    kpi.id === action.payload.userKpiId
                        ? {
                            ...kpi,
                            goals: (kpi.goals || []).map(goal =>
                                goal.id === action.payload.id ? action.payload : goal
                            )
                          }
                        : kpi
                ),
                // Update userKpiWithGoals if it's the same user KPI
                userKpiWithGoals: state.userKpiWithGoals && state.userKpiWithGoals.id === action.payload.userKpiId
                    ? {
                        ...state.userKpiWithGoals,
                        goals: (state.userKpiWithGoals.goals || []).map(goal =>
                            goal.id === action.payload.id ? action.payload : goal
                        )
                      }
                    : state.userKpiWithGoals,
                error: null
            };

        case types.UPDATE_MULTIPLE_GOALS_SUCCESS:
            return {
                ...state,
                dialogLoading: false,
                userKpiGoals: state.userKpiGoals.map(goal => {
                    const updated = action.payload.updatedGoals.find(g => g.id === goal.id);
                    return updated ? { ...goal, ...updated } : goal;
                }),
                // Update multiple goals in the main userKpis array
                userKpis: state.userKpis.map(kpi => {
                    const hasUpdatedGoals = action.payload.updatedGoals.some(g => g.userKpiId === kpi.id);
                    if (hasUpdatedGoals) {
                        return {
                            ...kpi,
                            goals: (kpi.goals || []).map(goal => {
                                const updated = action.payload.updatedGoals.find(g => g.id === goal.id);
                                return updated ? { ...goal, ...updated } : goal;
                            })
                        };
                    }
                    return kpi;
                }),
                // Update userKpiWithGoals if it contains updated goals
                userKpiWithGoals: state.userKpiWithGoals && state.userKpiWithGoals.goals
                    ? {
                        ...state.userKpiWithGoals,
                        goals: state.userKpiWithGoals.goals.map(goal => {
                            const updated = action.payload.updatedGoals.find(g => g.id === goal.id);
                            return updated ? { ...goal, ...updated } : goal;
                        })
                      }
                    : state.userKpiWithGoals,
                error: null
            };

        case types.REMOVE_GOAL_FROM_USER_KPI_SUCCESS:
            return {
                ...state,
                dialogLoading: false,
                userKpiGoals: state.userKpiGoals.filter(goal => goal.id !== action.payload),
                // Update the goals count in the main userKpis array
                userKpis: state.userKpis.map(kpi => {
                    const goalToRemove = state.userKpiGoals.find(g => g.id === action.payload);
                    if (goalToRemove && kpi.id === goalToRemove.userKpiId) {
                        return { ...kpi, goals: (kpi.goals || []).filter(g => g.id !== action.payload) };
                    }
                    return kpi;
                }),
                // Update userKpiWithGoals if it's the same user KPI
                userKpiWithGoals: state.userKpiWithGoals && state.userKpiWithGoals.goals
                    ? { ...state.userKpiWithGoals, goals: state.userKpiWithGoals.goals.filter(g => g.id !== action.payload) }
                    : state.userKpiWithGoals,
                error: null
            };

        case types.FETCH_USER_KPI_GOALS_SUCCESS:
            return {
                ...state,
                dialogLoading: false,
                userKpiGoals: action.payload,
                error: null
            };

        case types.FETCH_USER_KPIS_FAILURE:
        case types.ASSIGN_USER_KPI_FAILURE:
        case types.DELETE_USER_KPI_FAILURE:
        case types.UPDATE_USER_KPI_FAILURE:
        case types.FETCH_USER_KPI_BY_ID_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload
            };

        case types.FETCH_USER_KPI_WITH_GOALS_FAILURE:
        case types.ADD_GOAL_TO_USER_KPI_FAILURE:
        case types.UPDATE_USER_KPI_GOAL_FAILURE:
        case types.UPDATE_MULTIPLE_GOALS_FAILURE:
        case types.REMOVE_GOAL_FROM_USER_KPI_FAILURE:
        case types.FETCH_USER_KPI_GOALS_FAILURE:
            return {
                ...state,
                dialogLoading: false,
                error: action.payload
            };

        case types.SET_USER_KPI_FILTERS:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    ...action.payload
                }
            };

        case types.SET_USER_KPI_PAGINATION:
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    ...action.payload
                }
            };

        case types.RESET_USER_KPI_FILTERS:
            return {
                ...state,
                filters: initialState.filters,
                pagination: initialState.pagination
            };

        case types.OPEN_USER_KPI_DIALOG:
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    userKpiDialog: true
                },
                dialogData: {
                    ...state.dialogData,
                    userKpi: action.payload
                }
            };

        case types.CLOSE_USER_KPI_DIALOG:
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    userKpiDialog: false
                },
                dialogData: {
                    ...state.dialogData,
                    userKpi: null
                }
            };

        case types.OPEN_MANAGE_GOALS_DIALOG:
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    manageGoalsDialog: true
                },
                dialogData: {
                    ...state.dialogData,
                    userKpi: action.payload
                }
            };

        case types.CLOSE_MANAGE_GOALS_DIALOG:
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    manageGoalsDialog: false
                },
                userKpiWithGoals: null,
                userKpiGoals: [],
                dialogData: {
                    ...state.dialogData,
                    userKpi: null
                }
            };

        case types.OPEN_DELETE_DIALOG:
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    deleteDialog: true
                },
                dialogData: {
                    ...state.dialogData,
                    userKpiToDelete: action.payload
                }
            };

        case types.CLOSE_DELETE_DIALOG:
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    deleteDialog: false
                },
                dialogData: {
                    ...state.dialogData,
                    userKpiToDelete: null
                }
            };

        case types.OPEN_EDIT_USER_KPI_DIALOG:
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    editUserKpiDialog: true
                },
                dialogData: {
                    ...state.dialogData,
                    userKpi: action.payload
                }
            };

        case types.CLOSE_EDIT_USER_KPI_DIALOG:
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    editUserKpiDialog: false
                },
                dialogData: {
                    ...state.dialogData,
                    userKpi: null
                }
            };

        case types.OPEN_SCORE_DIALOG:
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    scoreDialog: true
                },
                dialogData: {
                    ...state.dialogData,
                    userKpi: action.payload
                }
            };

        case types.CLOSE_SCORE_DIALOG:
            return {
                ...state,
                dialogs: {
                    ...state.dialogs,
                    scoreDialog: false
                },
                userKpiWithGoals: null,
                userKpiScores: [],
                dialogData: {
                    ...state.dialogData,
                    userKpi: null
                }
            };

        case types.FETCH_USER_KPI_SCORES_SUCCESS:
            return {
                ...state,
                dialogLoading: false,
                userKpiScores: (action.payload || []).map(normalizeScoreDate),
                error: null
            };

        case types.CREATE_BULK_SCORES_SUCCESS:
            return {
                ...state,
                userKpiScores: [
                    ...state.userKpiScores,
                    ...(Array.isArray(action.payload) ? action.payload : []).map(normalizeScoreDate)
                ],
                error: null
            };

        case types.CREATE_SCORE_SUCCESS:
            return {
                ...state,
                userKpiScores: [...state.userKpiScores, normalizeScoreDate(action.payload.data)],
                error: null
            };

        case types.UPDATE_SCORE_SUCCESS:
            return {
                ...state,
                userKpiScores: state.userKpiScores.map(score =>
                    score.id === action.payload.data.id
                        ? normalizeScoreDate(action.payload.data)
                        : score
                ),
                error: null
            };

        case types.DELETE_SCORE_SUCCESS:
            // Remove the deleted score from the array (no loading change)
            return {
                ...state,
                userKpiScores: state.userKpiScores.filter(score => score.id !== action.payload),
                error: null
            };

        case types.FETCH_USER_KPI_SCORES_FAILURE:
            return {
                ...state,
                dialogLoading: false,
                error: action.payload
            };

        case types.CREATE_BULK_SCORES_FAILURE:
        case types.CREATE_SCORE_FAILURE:
        case types.UPDATE_SCORE_FAILURE:
        case types.DELETE_SCORE_FAILURE:
            return {
                ...state,
                error: action.payload
            };

        case types.SET_SELECTED_USER_KPI:
            return {
                ...state,
                selectedUserKpi: action.payload
            };

        case types.CLEAR_SELECTED_USER_KPI:
            return {
                ...state,
                selectedUserKpi: null
            };

        // Dropdown Data Actions
        case types.FETCH_ALL_USERS_REQUEST:
        case types.FETCH_USERS_FOR_EDIT_REQUEST:
        case types.FETCH_ALL_PERIODS_REQUEST:
        case types.FETCH_ALL_TEMPLATES_REQUEST:
        case types.FETCH_ALL_GOALS_REQUEST:
        case types.FETCH_ALL_DEPARTMENTS_REQUEST:
        case types.FETCH_ALL_REPORTING_MANAGERS_REQUEST:
        case types.FETCH_DIRECT_LINE_MANAGERS_REQUEST:
            return {
                ...state,
                loadingDropdowns: true
            };

        case types.FETCH_ALL_USERS_SUCCESS:
            return {
                ...state,
                allUsers: action.payload,
                loadingDropdowns: false
            };

        case types.FETCH_USERS_FOR_EDIT_SUCCESS:
            return {
                ...state,
                usersForEdit: action.payload,
                loadingDropdowns: false
            };

        case types.FETCH_ALL_PERIODS_SUCCESS:
            return {
                ...state,
                allPeriods: action.payload,
                loadingDropdowns: false
            };

        case types.FETCH_ALL_TEMPLATES_SUCCESS:
            return {
                ...state,
                allTemplates: action.payload,
                loadingDropdowns: false
            };

        case types.FETCH_ALL_GOALS_SUCCESS:
            return {
                ...state,
                allGoals: action.payload,
                loadingDropdowns: false
            };

        case types.FETCH_ALL_DEPARTMENTS_SUCCESS:
            return {
                ...state,
                allDepartments: action.payload,
                loadingDropdowns: false
            };

        case types.FETCH_ALL_DESIGNATIONS_SUCCESS:
            return {
                ...state,
                allDesignations: action.payload,
                loadingDropdowns: false
            };

        case types.FETCH_ALL_REPORTING_MANAGERS_SUCCESS:
            return {
                ...state,
                allReportingManagers: action.payload,
                loadingDropdowns: false
            };

        case types.FETCH_DIRECT_LINE_MANAGERS_SUCCESS:
            return {
                ...state,
                allDirectLineManagers: action.payload,
                loadingDropdowns: false
            };

        case types.FETCH_ALL_USERS_FAILURE:
        case types.FETCH_USERS_FOR_EDIT_FAILURE:
        case types.FETCH_ALL_PERIODS_FAILURE:
        case types.FETCH_ALL_TEMPLATES_FAILURE:
        case types.FETCH_ALL_GOALS_FAILURE:
        case types.FETCH_ALL_DEPARTMENTS_FAILURE:
        case types.FETCH_ALL_DESIGNATIONS_FAILURE:
        case types.FETCH_ALL_REPORTING_MANAGERS_FAILURE:
        case types.FETCH_DIRECT_LINE_MANAGERS_FAILURE:
            return {
                ...state,
                loadingDropdowns: false,
                error: action.payload
            };

        default:
            return state;
    }
};

export default userKpiReducer;
